import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    MainHeaderBackgroundColorConstantType,
    MainHeaderSeparatorConstantType,
    MainHeaderDynamicStyleType
} from "./type";
import type {
    BreakpointType,
    Mutable
} from "../../types";
import {
    HEADER_SCROLL_EDGES,
    getHeaderScrollEdge,
    getHeaderHeight
} from "../header/stylesheet";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";

export const MAIN_HEADER_PADDINGS: Record<BreakpointType, keyof NCoreUIKit.Spaces> = {
    compact: "spacingSm",
    medium: "spacingLg",
    wide: "spacingLg"
};

export const getMainHeaderBackgroundColor = ({
    customBackgroundColor,
    backgroundColor,
    colors
}: MainHeaderBackgroundColorConstantType): string => {
    if(customBackgroundColor) {
        return colors.project[customBackgroundColor];
    }

    return colors.content.container[backgroundColor];
};

export const getIsMainHeaderSeparatorVisible = ({
    isWorkWithSeparator,
    isScrolled
}: MainHeaderSeparatorConstantType): boolean => {
    if(isWorkWithSeparator !== undefined) {
        return isWorkWithSeparator;
    }

    return HEADER_SCROLL_EDGES[getHeaderScrollEdge({
        isScrolled
    })].isSeparatorVisible;
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        width: "100%"
    },
    center: {
        justifyContent: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        overflow: "hidden",
        minWidth: 0,
        flex: 1
    },
    baseContainer: {
        flexDirection: "column",
        flex: 1
    },
    menuBody: {
        flexDirection: "column"
    },
    centerContent: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0
    },
    stickyContainer: {
        position: "absolute",
        start: 0,
        end: 0,
        top: 0
    },
    actions: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0
    },
    switchers: {
        flexDirection: "row",
        alignItems: "center"
    },
    menuSwitchers: {
        flexWrap: "wrap"
    },
    stickySpacer: {
        width: "100%"
    },
    siteLogo: {
        flexShrink: 1
    }
});

export const useStyles = ({
    customBackgroundColor,
    isWorkWithSeparator,
    backgroundColor,
    controlHeights,
    contentHeight,
    shapeConfigs,
    shadowAlpha,
    breakpoint,
    elevations,
    isScrolled,
    borders,
    colors,
    spaces
}: MainHeaderDynamicStyleType) => {
    const currentScrollEdge = HEADER_SCROLL_EDGES[getHeaderScrollEdge({
        isScrolled
    })];

    const currentBackgroundColor = getMainHeaderBackgroundColor({
        customBackgroundColor,
        backgroundColor,
        colors
    });

    const isSeparatorVisible = getIsMainHeaderSeparatorVisible({
        isWorkWithSeparator,
        isScrolled
    });

    const styles = {
        container: {
            borderBottomColor: isSeparatorVisible ? colors.content.border.subtle : "transparent",
            paddingHorizontal: spaces[MAIN_HEADER_PADDINGS[breakpoint]],
            backgroundColor: currentBackgroundColor,
            borderBottomWidth: borders.separator,
            zIndex: shapeConfigs.layers.raised,
            minHeight: getHeaderHeight({
                controlHeights,
                spaces
            }),
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        stickySpacer: {
            backgroundColor: currentBackgroundColor,
            height: contentHeight
        } as Mutable<ViewStyle>,
        safeAreaView: {
            backgroundColor: currentBackgroundColor,
            zIndex: shapeConfigs.layers.raised
        } as Mutable<ViewStyle>,
        stickyContainer: {
            zIndex: shapeConfigs.layers.sticky
        } as Mutable<ViewStyle>,
        separator: {
            marginHorizontal: spaces.spacingXs
        } as Mutable<ViewStyle>,
        actions: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        menuBody: {
            gap: spaces.spacingLg
        } as Mutable<ViewStyle>,
        switchers: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(elevations[currentScrollEdge.level].length) {
        Object.assign(styles.container, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: currentScrollEdge.level,
            shadowAlpha,
            elevations
        }));
    }

    return styles;
};

export default stylesheet;
